import { Component, Show, createEffect, createMemo, createSignal } from "solid-js"
import { isGlobalMutedAmbient, setGlobalMuteAmbient } from "../lib/ambient-audio"
import { pauseAnimation, skipSeconds } from "../lib/animation"
import {
    loadCustomScenario,
    loadDefaultScenario,
    updateCurrentScenario,
} from "../lib/scenario-loader"
import { isGlobalMutedTimeline, setGlobalMuteTimeline } from "../lib/timeline-audio"
import { getUserUUID } from "../lib/uuid"
import { handleViewTransition } from "../lib/view-transition"
import { useAppStore } from "../stores/app"
import { clock } from "../stores/clock"
import { useScenarioStore } from "../stores/scenario"
import type { AppMode, Project, ScenarioMode } from "../types"
import { formatSimTime } from "../types"
import SaveBeforeLoadModal from "./SaveBeforeLoadModal"
import ScenarioSelectionModal, { type ScenarioConfig } from "./ScenarioSelectionModal"
import ShareModal from "./ShareModal"

interface ToolbarProps {
    onModeChange: (mode: AppMode) => void
    onOpenMobileMenu?: () => void
    onScenarioModeChange: (mode: ScenarioMode, name: string) => void
}

const Toolbar: Component<ToolbarProps> = (props) => {
    // Get scenario store for persistence and loading
    const [scenario, scenarioActions] = useScenarioStore()
    const [app, appActions] = useAppStore()
    
    const [showTimeSetter, setShowTimeSetter] = createSignal(false)
    const [showScenarioModal, setShowScenarioModal] = createSignal(false)
    const [showSaveBeforeNew, setShowSaveBeforeNew] = createSignal(false)
    const [isMuted, setIsMuted] = createSignal(isGlobalMutedAmbient() || isGlobalMutedTimeline())

    // Create a memo to ensure reactivity for analysis sections
    const hasAnalysis = createMemo(() => scenario.analysis.sections.length > 0)

    // Hide time setter when animation starts running
    createEffect(() => {
        if (clock.isRunning) {
            setShowTimeSetter(false)
        }
    })
    const [isSharing, setIsSharing] = createSignal(false)
    const [showShareModal, setShowShareModal] = createSignal(false)

    const handleToggleMute = () => {
        const newMutedState = !isMuted()
        setIsMuted(newMutedState)
        setGlobalMuteAmbient(newMutedState)
        setGlobalMuteTimeline(newMutedState)
    }

    const handleShareLink = async (): Promise<string> => {
        setIsSharing(true)
        try {
            const userUUID = getUserUUID()
            const baseUrl = (
                import.meta.env.VITE_BASEURL_API || "https://sim86-worker.mycolorpicker.workers.dev"
            ).replace(/\/$/, "")

            const now = new Date().toISOString()
            const projectToShare: Project = {
                meta: {
                    ...scenario.meta,
                    ownerUUID: userUUID,
                    createdAt: scenario.meta.createdAt || now,
                    updatedAt: now,
                },
                agents: JSON.parse(JSON.stringify(scenario.agents)),
                clockAnnotations: JSON.parse(JSON.stringify(scenario.clockAnnotations)),
                speedControls: JSON.parse(JSON.stringify(scenario.speedControls)),
                analysis: JSON.parse(JSON.stringify(scenario.analysis)),
                viewportPins: JSON.parse(JSON.stringify(scenario.viewportPins)),
                autoPan: scenario.autoPan,
            }

            const response = await fetch(`${baseUrl}/scenarios`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(projectToShare),
            })

            if (!response.ok) {
                const errorBody = await response.text()
                console.error("Share request failed:", {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorBody,
                })
                throw new Error(`Failed to share: ${response.statusText}`)
            }

            const { slug } = await response.json()

            // Update meta with slug
            scenarioActions.updateMeta({
                slug,
                ownerUUID: userUUID,
                createdAt: projectToShare.meta.createdAt,
            })

            // Save updated meta to localStorage
            scenarioActions.saveToLocalStorage()

            // Update URL without reload
            window.history.pushState({}, "", `/${slug}`)

            const url = `${window.location.origin}/${slug}`
            return url
        } catch (err) {
            console.error("Failed to share scenario:", err)
            throw err
        } finally {
            setIsSharing(false)
        }
    }

    const handleDownloadFile = () => {
        try {
            const project: Project = {
                meta: {
                    ...scenario.meta,
                    exportedAt: new Date().toISOString(),
                },
                agents: JSON.parse(JSON.stringify(scenario.agents)),
                clockAnnotations: JSON.parse(JSON.stringify(scenario.clockAnnotations)),
                speedControls: JSON.parse(JSON.stringify(scenario.speedControls)),
                analysis: JSON.parse(JSON.stringify(scenario.analysis)),
                viewportPins: JSON.parse(JSON.stringify(scenario.viewportPins)),
                autoPan: scenario.autoPan,
            }

            const json = JSON.stringify(project, null, 2)
            const blob = new Blob([json], { type: "application/json" })
            const url = URL.createObjectURL(blob)

            const a = document.createElement("a")
            a.href = url
            const scenarioName = scenario.meta.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")
            const date = new Date().toISOString().split("T")[0]
            a.download = `scenario-${scenarioName}-${date}.json`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)

            // Close modal after download
            setShowShareModal(false)
        } catch (err) {
            console.error("Failed to download scenario:", err)
            alert("Det gick inte att spara filen. Försök igen.")
        }
    }
    const openScenarioModal = () => {
        pauseAnimation()
        const popups = document.querySelectorAll(".clock-popup")
        popups.forEach((p) => p.remove())
        setShowScenarioModal(true)
    }

    const handleOpenModal = () => {
        if (app.isEditable) {
            setShowSaveBeforeNew(true)
        } else {
            openScenarioModal()
        }
    }

    const handleScenarioSelect = (config: ScenarioConfig) => {
        if (config.type === "template") {
            if (!config.editable) {
                // Load template as read-only
                loadDefaultScenario(scenarioActions, config.templateData!)
                props.onScenarioModeChange("default", config.templateData!.meta?.name || "Standardscenario")
                window.history.pushState({}, "", "/")
            } else {
                // Load template as editable copy
                const name = config.scenarioName || config.templateData!.meta?.name || "Anpassat scenario"
                const metaOverrides = {
                    name,
                    startTime: config.globalClockStart,
                    crimeTime: config.crimeTime,
                    soundEffects: config.soundEffectsEnabled,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                }
                loadCustomScenario(scenarioActions, config.templateData!, metaOverrides)
                props.onScenarioModeChange("custom", name)
                localStorage.setItem("hasCustomScenario", "true")
                scenarioActions.saveToLocalStorage()
                window.history.pushState({}, "", "/")
            }
        } else if (config.type === "clone-current") {
            // Clone current scenario — keep agents/annotations but update meta
            const name = config.scenarioName || "Anpassat scenario"
            localStorage.setItem("hasCustomScenario", "true")
            updateCurrentScenario(scenarioActions, {
                name,
                startTime: config.globalClockStart,
                crimeTime: config.crimeTime,
                soundEffects: config.soundEffectsEnabled,
            })
            props.onScenarioModeChange("custom", name)
            window.history.pushState({}, "", "/")
        } else if (config.type === "from-file") {
            if (!config.fileData) {
                console.error("No file data provided")
                return
            }
            try {
                const name = config.fileData.meta?.name || "Importerat scenario"
                loadCustomScenario(scenarioActions, config.fileData, {
                    name,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                })
                props.onScenarioModeChange("custom", name)
                localStorage.setItem("hasCustomScenario", "true")
                scenarioActions.saveToLocalStorage()
                window.history.pushState({}, "", "/")
            } catch (err) {
                console.error("Failed to load scenario from file:", err)
                alert("Det gick inte att ladda scenariofilen. Kontrollera att filen är giltig.")
            }
        }
    }
    const adjustMinute = (delta: number) => {
        // Use skipSeconds to ensure proper audio/popup cleanup
        skipSeconds(delta * 60)
    }

    const adjustSecond = (delta: number) => {
        // Use skipSeconds to ensure proper audio/popup cleanup
        skipSeconds(delta)
    }

    return (
        <div
            class="flex items-center justify-between border-b border-gray-700 bg-gray-900 px-4 py-2 text-white"
            style={{ "padding-top": "calc(0.5rem + env(safe-area-inset-top))" }}
        >
            {/* Mobile layout */}
            <div class="flex w-full items-center justify-between md:hidden">
                {/* Hamburger menu */}
                <button class="rounded p-2 transition-colors hover:bg-gray-800" onClick={props.onOpenMobileMenu}>
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 6h16M4 12h16M4 18h16"
                        />
                    </svg>
                </button>

                {/* Analysis button - prominent on mobile */}
                <Show when={hasAnalysis()}>
                    <button
                        class="hidden touch-manipulation rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-blue-500 active:bg-blue-700"
                        onClick={() => handleViewTransition(() => appActions.openModal("analysis"))}
                    >
                        📊 Analys
                    </button>
                </Show>

                {/* Clock (centered) */}
                <div class="font-mono text-xl tracking-wider tabular-nums select-none">
                    {formatSimTime(clock.currentTime)}
                </div>
            </div>
            {/* Desktop layout */}
            <div class="hidden items-center gap-2 md:flex">
                <h1 class="mr-1 text-xl font-bold tracking-tight">Sim86.se</h1>

                {/* Scenario mode indicator */}
                <span class="py-0.5 pr-4 pl-0.5 text-xs text-white">{scenario.meta.name}</span>

                {/* Only show Simulera/Edit buttons when editable (has localStorage) */}
                <Show when={app.isEditable}>
                    <button
                        class={`rounded px-3 py-0.5 text-sm font-medium transition-colors ${
                            app.mode === "simulate"
                                ? "bg-green-600 text-white"
                                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }`}
                        onClick={() => props.onModeChange("simulate")}
                    >
                        Simulera
                    </button>
                    <button
                        class={`rounded px-3 py-0.5 text-sm font-medium transition-colors ${
                            app.mode === "edit"
                                ? "bg-blue-600 text-white"
                                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }`}
                        onClick={() => props.onModeChange("edit")}
                    >
                        Redigera
                    </button>
                </Show>

                <label class="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-400 select-none">
                    <input
                        type="checkbox"
                        checked={app.showLabels}
                        onChange={() => appActions.toggleShowLabels()}
                        class="h-3 w-3 accent-gray-500"
                    />
                    Etiketter
                </label>
                <label class="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-400 select-none">
                    <input
                        type="checkbox"
                        checked={isMuted()}
                        onChange={handleToggleMute}
                        class="h-3 w-3 accent-gray-500"
                    />
                    Ljud av
                </label>
                <Show when={scenario.viewportPins.length > 1}>
                    <label class="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-400 select-none">
                        <input
                            type="checkbox"
                            checked={scenario.autoPan}
                            onChange={() => {
                                scenarioActions.setAutoPan(!scenario.autoPan)
                                scenarioActions.scheduleSave()
                            }}
                            class="h-3 w-3 accent-gray-500"
                        />
                        Auto-panorera
                    </label>
                </Show>
            </div>{" "}
            <div class="hidden items-center gap-3 md:flex">
                {/* Clock display with optional time setter */}
                <div class="flex items-center gap-1">
                    <Show when={showTimeSetter()}>
                        <div class="mr-1 flex items-center gap-0.5">
                            <div class="flex flex-col">
                                <button
                                    class="text-[10px] leading-none text-gray-400 hover:text-white"
                                    onClick={() => adjustMinute(1)}
                                >
                                    ▲
                                </button>
                                <button
                                    class="text-[10px] leading-none text-gray-400 hover:text-white"
                                    onClick={() => adjustMinute(-1)}
                                >
                                    ▼
                                </button>
                            </div>
                            <span class="mr-1 text-xs text-gray-500">min</span>
                            <div class="flex flex-col">
                                <button
                                    class="text-[10px] leading-none text-gray-400 hover:text-white"
                                    onClick={() => adjustSecond(10)}
                                >
                                    ▲
                                </button>
                                <button
                                    class="text-[10px] leading-none text-gray-400 hover:text-white"
                                    onClick={() => adjustSecond(-10)}
                                >
                                    ▼
                                </button>
                            </div>
                            <span class="text-xs text-gray-500">sek</span>
                        </div>
                    </Show>

                    <div class="font-mono text-xl tracking-wider tabular-nums">{formatSimTime(clock.currentTime)}</div>

                    <button
                        class={`ml-1 rounded px-2 py-1 text-xs transition-colors ${
                            showTimeSetter() ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600 active:scale-105 duration-100"
                        }`}
                        onClick={() => {
                            pauseAnimation()
                            setShowTimeSetter((v) => !v)
                        }}
                        title="Justera klockans starttid"
                    >
                        Tid
                    </button>
                </div>

                <div class="flex gap-2">
                    <Show when={hasAnalysis()}>
                        <button
                            class="rounded bg-gray-700 px-2 py-1 text-xs  hover:bg-gray-600 transition-colors  duration-50 active:scale-105"
                            onClick={() => handleViewTransition(() => appActions.openModal("analysis"))}
                            title="Visa analys"
                        >
                            Analys
                        </button>
                    </Show>
                    <Show when={app.isEditable}>
                        <button
                            class="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                            onClick={() => setShowShareModal(true)}
                            title="Dela det här scenariot"
                        >
                            Dela
                        </button>
                    </Show>
                    <button
                        class="rounded bg-gray-700 px-2 py-1 text-xs hover:bg-gray-600 active:scale-105 transition-colors duration-100"
                        onClick={handleOpenModal}
                        title="Starta nytt scenario"
                    >
                        Nytt scenario
                    </button>
                </div>
            </div>
            <ScenarioSelectionModal
                isOpen={showScenarioModal()}
                onClose={() => setShowScenarioModal(false)}
                onSelect={handleScenarioSelect}
                currentMeta={scenario.meta}
            />
            <SaveBeforeLoadModal
                isOpen={showSaveBeforeNew()}
                onSave={async () => {
                    setShowSaveBeforeNew(false)
                    try { await handleShareLink() } catch { /* proceed even if share fails */ }
                    openScenarioModal()
                }}
                onDiscard={() => { setShowSaveBeforeNew(false); openScenarioModal() }}
                onCancel={() => setShowSaveBeforeNew(false)}
            />
            <ShareModal
                isOpen={showShareModal()}
                onClose={() => setShowShareModal(false)}
                onShareLink={handleShareLink}
                onDownloadFile={handleDownloadFile}
                isSharing={isSharing()}
            />
        </div>
    )
}

export default Toolbar
